class ProductCarouselTabs extends window.BAO.CustomElement() {
  static FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'audio[controls]',
    'video[controls]',
    'summary',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]',
  ].join(',')

  static ORIGINAL_TABINDEX_ATTRIBUTE =
    'data-product-carousel-tabs-original-tabindex'

  connectedCallback () {
    super.connectedCallback()

    this.ensureLiveRegion()
    this.syncA11yState()
  }

  setupListeners () {
    super.setupListeners()

    this.handleTabChange = this.handleTabChange.bind(this)

    this.els.trigger?.elements.forEach((triggerEl) => {
      this.listeners.add(triggerEl, 'click', this.handleTabChange)
    })
  }

  handleTabChange (event) {
    const clickedTrigger = event.currentTarget

    this.els.trigger?.elements?.forEach((triggerEl) => {
      const isActive = triggerEl.dataset.index === clickedTrigger.dataset.index

      triggerEl.setAttribute('aria-selected', String(isActive))
      triggerEl.setAttribute('tabindex', isActive ? '0' : '-1')
    })

    this.els.link?.elements.forEach((linkEl) => {
      linkEl.setAttribute(
        'aria-hidden',
        linkEl.dataset.index !== clickedTrigger.dataset.index,
      )
    })

    this.els.panel?.elements.forEach((panelEl) => {
      const isActive = panelEl.dataset.index === clickedTrigger.dataset.index

      this.setPanelState(panelEl, isActive)
    })

    this.announceSelection(clickedTrigger)
  }

  syncA11yState () {
    this.els.trigger?.elements?.forEach((triggerEl) => {
      const isActive = triggerEl.getAttribute('aria-selected') === 'true'
      triggerEl.setAttribute('tabindex', isActive ? '0' : '-1')
    })

    this.els.panel?.elements?.forEach((panelEl) => {
      const isHidden = panelEl.getAttribute('aria-hidden') === 'true'

      this.setPanelState(panelEl, !isHidden)
    })
  }

  setPanelState (panelEl, isActive) {
    panelEl.setAttribute('aria-hidden', String(!isActive))
    panelEl.setAttribute('tabindex', isActive ? '0' : '-1')

    this.setFocusableState(panelEl, !isActive)
  }

  setFocusableState (containerEl, isHidden) {
    const focusableElements = containerEl.querySelectorAll(
      ProductCarouselTabs.FOCUSABLE_SELECTOR,
    )

    focusableElements.forEach((element) => {
      if (isHidden) {
        if (
          !element.hasAttribute(ProductCarouselTabs.ORIGINAL_TABINDEX_ATTRIBUTE)
        ) {
          const originalTabindex = element.getAttribute('tabindex')

          element.setAttribute(
            ProductCarouselTabs.ORIGINAL_TABINDEX_ATTRIBUTE,
            originalTabindex ?? '',
          )
        }

        element.setAttribute('tabindex', '-1')

        return
      }

      if (
        !element.hasAttribute(ProductCarouselTabs.ORIGINAL_TABINDEX_ATTRIBUTE)
      ) {
        return
      }

      const originalTabindex = element.getAttribute(
        ProductCarouselTabs.ORIGINAL_TABINDEX_ATTRIBUTE,
      )

      if (originalTabindex === '') {
        element.removeAttribute('tabindex')
      } else {
        element.setAttribute('tabindex', originalTabindex)
      }

      element.removeAttribute(ProductCarouselTabs.ORIGINAL_TABINDEX_ATTRIBUTE)
    })
  }

  ensureLiveRegion () {
    if (this.liveRegion) {
      return
    }

    const existingLiveRegion = this.querySelector(
      '[data-product-carousel-tabs-el="live-region"]',
    )

    if (existingLiveRegion) {
      this.liveRegion = existingLiveRegion
      return
    }

    const liveRegion = document.createElement('span')
    liveRegion.className = 'util-ScreenReaderOnly'
    liveRegion.setAttribute('role', 'status')
    liveRegion.setAttribute('aria-live', 'polite')
    liveRegion.setAttribute('aria-atomic', 'true')
    liveRegion.setAttribute('data-product-carousel-tabs-el', 'live-region')

    this.appendChild(liveRegion)
    this.liveRegion = liveRegion
  }

  announceSelection (triggerEl) {
    if (!this.liveRegion || !triggerEl) {
      return
    }

    const tabLabel = (triggerEl.textContent || '').trim()
    if (!tabLabel) {
      return
    }

    const message = `Selected tab ${tabLabel}`

    if (this.liveRegion.textContent === message) {
      this.liveRegion.textContent = ''
    }

    this.liveRegion.textContent = message
  }
}

if (!customElements.get('product-carousel-tabs')) {
  customElements.define('product-carousel-tabs', ProductCarouselTabs)
}
